import type {
  ConciseWorkspace,
  PutWorkspaceSpecification,
} from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryWorkspacesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryWorkspacesService'
import type { ConfigurableServer } from '@festina-life/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { fetchAllConciseWorkspacesKey } from '../keys/configuration'
import type { AllConciseWorkspaces } from './useFetchAllConciseWorkspaces'

type CreateWorkspaceInputArguments = {
  server: ConfigurableServer
  name: string
  specification: PutWorkspaceSpecification
  shouldUpdateCache?: boolean
}

export const useCreateOrUpdateConciseWorkspace = (
  availableServers: ConfigurableServer[],
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      name,
      server,
      specification,
    }: CreateWorkspaceInputArguments) => {
      await ConfigurationRepositoryWorkspacesService.workspaceServicePutPutWorkspace(
        {
          workspaceName: name,
          componentName: server.toLocaleLowerCase(),
          requestBody: specification,
        },
      )
    },
    onSuccess: (_, variables) => {
      if (variables.shouldUpdateCache) {
        queryClient.setQueryData(
          fetchAllConciseWorkspacesKey(availableServers),
          (currentData: AllConciseWorkspaces) => {
            const serverWorkspaces = currentData[variables.server] ?? []
            const isExistentWorkspace = serverWorkspaces.find(
              (workspace) => workspace.name === variables.name,
            )

            const nextServerWorkspaces = isExistentWorkspace
              ? serverWorkspaces.map((workspace) =>
                  workspace.name === variables.name
                    ? { ...workspace, ...variables.specification }
                    : workspace,
                )
              : [
                  ...serverWorkspaces,
                  {
                    ...variables.specification,
                    name: variables.name,
                    _type: 'ConciseWorkspace',
                  } satisfies ConciseWorkspace,
                ]

            return {
              ...currentData,
              [variables.server]: nextServerWorkspaces,
            }
          },
        )
      }
    },
  })
}
